import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import * as Icons from "lucide-react";
import { toast } from "sonner";
import { adapter } from "@/services/db";
import { useAuth } from "@/context/AuthContext";
import { verifyProfilePw } from "@/services/ccApi";
import { restoreValue, afterSer, ser, logEdit } from "./audit";

const isDocLevel = (a) => a === "create_document" || a === "delete_document";

// Profile-password-gated undo of one audit entry. Asked every time.
export function UndoDialog({ path, id, entry, sensitive, onClose }) {
  const { user } = useAuth();
  const [current, setCurrent] = useState(undefined);
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setPw(""); setCurrent(undefined); if (entry) adapter.get(path, id).then(setCurrent); }, [entry, path, id]);
  if (!entry) return null;

  const docLevel = isDocLevel(entry.action);
  const curVal = docLevel ? (current ? { ...current, id: undefined } : undefined) : current?.[entry.field];
  const curSer = docLevel ? (current ? "exists" : "absent") : ser(curVal);
  const drifted = docLevel
    ? (entry.action === "create_document" ? !current : !!current)
    : curSer !== afterSer(entry);

  const run = async () => {
    setBusy(true);
    try {
      const r = await verifyProfilePw(pw).catch((e) => ({ ok: false, err: e.response?.data?.detail }));
      if (!r.ok) { toast.error(r.err || "Incorrect profile password"); return; }
      const { value } = restoreValue(entry);
      if (entry.action === "create_document") await adapter.remove(path, id);
      else if (entry.action === "delete_document") await adapter.createDoc(path, id, Object.fromEntries(Object.entries(value || {}).filter(([k]) => k !== "id")));
      else {
        if (!current) throw new Error("Document no longer exists");
        await adapter.patchField(path, id, entry.field, value);
      }
      await logEdit(user, { path, id, action: "undo", field: entry.field || null, before: docLevel ? (current || undefined) : curVal, after: value, sensitive, extra: { undo_of: entry.id } });
      toast.success("Change undone");
      onClose();
    } catch (e) { toast.error(e.code === "permission-denied" ? "Permission denied by Firestore rules" : e.message); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="undo-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2"><Icons.Undo2 className="h-5 w-5 text-primary" /> Undo {entry.action}{entry.field ? ` · ${entry.field}` : ""}</DialogTitle>
          <DialogDescription>Restores the value from before this edit. Enter your System Control profile password.</DialogDescription>
        </DialogHeader>
        {drifted && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="undo-drift-warning">
            <p className="font-semibold mb-1 flex items-center gap-1"><Icons.AlertTriangle className="h-3.5 w-3.5" /> Changed since this edit — undo will overwrite the current value:</p>
            <pre className="font-mono whitespace-pre-wrap break-all max-h-40 overflow-auto" data-testid="undo-current-value">{docLevel ? curSer : curSer === "__absent__" ? "(field absent)" : curSer}</pre>
          </div>
        )}
        <Input type="password" autoFocus value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Profile password" data-testid="undo-password-input"
          onKeyDown={(e) => e.key === "Enter" && pw && !busy && run()} />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} data-testid="undo-cancel">Cancel</Button>
          <Button onClick={run} disabled={!pw || busy || current === undefined} data-testid="undo-confirm">{busy ? "Undoing…" : drifted ? "Overwrite & undo" : "Undo"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
